import { createContext, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { fetchMe, login as apiLogin, register as apiRegister } from "../api/auth";

const AuthContext = createContext(null);
const TOKEN_KEY = "chachophone_token";

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(() => Cookies.get(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false); // becomes true once the initial session check finishes

  useEffect(() => {
    if (!token) {
      setReady(true);
      return;
    }
    fetchMe(token)
      .then(({ user }) => setUser(user))
      .catch(() => {
        Cookies.remove(TOKEN_KEY);
        setToken("");
      })
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (credentials) => {
    const { token: newToken, user: newUser } = await apiLogin(credentials);
    Cookies.set(TOKEN_KEY, newToken, { expires: 30 });
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const register = async (details) => {
    const { token: newToken, user: newUser } = await apiRegister(details);
    Cookies.set(TOKEN_KEY, newToken, { expires: 30 });
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    Cookies.remove(TOKEN_KEY);
    setToken("");
    setUser(null);
  };

  const value = useMemo(
    () => ({ token, user, ready, isAuthenticated: !!user, isAdmin: !!user?.isAdmin, login, register, logout }),
    [token, user, ready]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
